import { useMemo, useState, useCallback } from 'react'
import { Popover, Checkbox } from 'antd'
import styled from 'styled-components'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT, BORDER_RADIUS } from '../../../styles/theme'
import type { BatchQuestion, NodeMetadataEntry } from '../hooks/useBatchQuestionState'

interface QuestionAttributionNoteProps {
  question: BatchQuestion
  metadataByNode: Record<string, NodeMetadataEntry>
  onChange: (next: {
    sourceNodeIds: string[]
    sourceNodeLabels: string[]
    sourceNodeCategories: string[]
  }) => void
}

const Note = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  /* 38px matches the indent used by TypeRow / ProbesSection / question text so
     the attribution chips visually align with the question content rather than
     floating against the card edge alongside the drag handle. */
  padding: 0 0 0 38px;
  margin: 0 0 ${SPACING.xs} 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.xs};
  font-family: inherit;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
  cursor: pointer;
  text-align: left;

  &:hover {
    color: var(--color-text-secondary);
  }
`

const NoteLabel = styled.span`
  text-transform: uppercase;
  font-size: 10px;
  font-weight: ${FONT_WEIGHT.semibold};
  letter-spacing: 0.6px;
  color: var(--color-text-tertiary);
  flex-shrink: 0;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: ${BORDER_RADIUS.full};
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  font-size: 11px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ChipCategory = styled.span`
  text-transform: uppercase;
  font-size: 9px;
  font-weight: ${FONT_WEIGHT.semibold};
  letter-spacing: 0.4px;
  color: var(--color-text-tertiary);
`

const MoreChip = styled.span`
  font-size: 11px;
  color: var(--color-text-tertiary);
  white-space: nowrap;
`

const UnattributedHint = styled.span`
  font-size: 11px;
  color: #d97706;
  font-style: italic;
`

const PopoverBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
  min-width: 280px;
`

const PopoverHeader = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  padding-bottom: ${SPACING.xs};
  border-bottom: 1px solid var(--color-border);
`

/** Plain div — antd Checkbox already wraps an input in its own <label>, so
 *  wrapping it again would create nested labels (invalid HTML, glitchy click). */
const PopoverRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.xs};
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-primary);
  user-select: none;

  &:hover {
    background: var(--color-bg-subtle, rgba(0, 0, 0, 0.02));
  }
`

const PopoverCategory = styled.span`
  text-transform: uppercase;
  font-size: 9px;
  font-weight: ${FONT_WEIGHT.semibold};
  letter-spacing: 0.4px;
  color: var(--color-text-tertiary);
  margin-top: 2px;
  flex-shrink: 0;
`

const PopoverLabel = styled.span`
  flex: 1;
  line-height: 1.35;
`

const PreviewChipsLimit = 2

function readAttribution(question: BatchQuestion): {
  ids: string[]
  labels: string[]
  categories: string[]
} {
  if (question.sourceNodeIds && question.sourceNodeIds.length > 0) {
    return {
      ids: question.sourceNodeIds,
      labels: question.sourceNodeLabels ?? question.sourceNodeIds.map(() => ''),
      categories: question.sourceNodeCategories ?? question.sourceNodeIds.map(() => ''),
    }
  }
  if (question.sourceNodeId) {
    return {
      ids: [question.sourceNodeId],
      labels: [question.sourceNodeLabel ?? ''],
      categories: [question.sourceNodeCategory ?? ''],
    }
  }
  return { ids: [], labels: [], categories: [] }
}

/**
 * Small "side note" rendered above the question editor card showing which
 * selected nodes the question provides evidence for. Clicking it opens a
 * popover with one checkbox per selected node, so users can re-attribute
 * questions they added or re-targeted without leaving the review screen.
 *
 * Reads metadata from `metadataByNode` (the same map used by the coverage
 * panel) so labels stay in sync even if the underlying question carries
 * stale strings (e.g. after a node was renamed).
 */
export function QuestionAttributionNote({
  question,
  metadataByNode,
  onChange,
}: QuestionAttributionNoteProps) {
  const [open, setOpen] = useState(false)
  const attribution = readAttribution(question)

  // Resolve labels/categories from current metadata when possible so the
  // chips don't drift if the user renames a node after generation.
  const resolved = useMemo(
    () =>
      attribution.ids.map((id, i) => {
        const meta = metadataByNode[id]
        return {
          id,
          label: meta?.nodeLabel ?? attribution.labels[i] ?? id,
          category: meta?.nodeCategory ?? attribution.categories[i] ?? '',
        }
      }),
    [attribution, metadataByNode]
  )

  const knownNodes = useMemo(
    () =>
      Object.entries(metadataByNode).map(([nodeId, meta]) => ({
        id: nodeId,
        label: meta.nodeLabel,
        category: meta.nodeCategory,
      })),
    [metadataByNode]
  )

  const selectedSet = useMemo(() => new Set(attribution.ids), [attribution.ids])

  const handleToggle = useCallback(
    (nodeId: string) => {
      const meta = metadataByNode[nodeId]
      if (!meta) return
      const isSelected = selectedSet.has(nodeId)
      const nextIds = isSelected
        ? attribution.ids.filter((id) => id !== nodeId)
        : [...attribution.ids, nodeId]
      const nextLabels = nextIds.map((id) => metadataByNode[id]?.nodeLabel ?? '')
      const nextCategories = nextIds.map((id) => metadataByNode[id]?.nodeCategory ?? '')
      onChange({
        sourceNodeIds: nextIds,
        sourceNodeLabels: nextLabels,
        sourceNodeCategories: nextCategories,
      })
    },
    [attribution.ids, metadataByNode, onChange, selectedSet]
  )

  const previewChips = resolved.slice(0, PreviewChipsLimit)
  const extra = resolved.length - previewChips.length

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      content={
        <PopoverBody>
          <PopoverHeader>This question tests…</PopoverHeader>
          {knownNodes.map((n) => (
            <PopoverRow key={n.id} onClick={() => handleToggle(n.id)}>
              <Checkbox
                checked={selectedSet.has(n.id)}
                // Click handler lives on the row so any tap (including label
                // text) toggles. We stop the propagation chain inside the
                // checkbox's own change event so antd doesn't double-toggle.
                onClick={(e) => e.stopPropagation()}
                onChange={() => handleToggle(n.id)}
                style={{ marginTop: 2 }}
              />
              <PopoverCategory>{n.category}</PopoverCategory>
              <PopoverLabel>{n.label}</PopoverLabel>
            </PopoverRow>
          ))}
        </PopoverBody>
      }
    >
      <Note type="button" aria-label="Edit attribution">
        <NoteLabel>Tests</NoteLabel>
        {resolved.length === 0 && <UnattributedHint>— add attribution</UnattributedHint>}
        {previewChips.map((c) => (
          <Chip key={c.id} title={c.label}>
            <ChipCategory>{c.category}</ChipCategory>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
          </Chip>
        ))}
        {extra > 0 && <MoreChip>+{extra} more</MoreChip>}
      </Note>
    </Popover>
  )
}
